
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import _Link from 'next/link'
import { Heading as _Heading } from '@/components/Heading'
import { DocumentationLayout as _Layout } from '@/layouts/DocumentationLayout'
import { ContentsLayout as _Default } from '@/layouts/ContentsLayout'
const Layout = _Layout;
const meta = {
  "id": "submit-template",
  "title": "Submitting Template to the Tradly Marketplace",
  "sidebar_label": "Submitting Templates"
};
const slug = 'submit-template';
const tableOfContents = [{
  "title": "Submitting Your Template to the Tradly Marketplace",
  "slug": "submitting-your-template-to-the-tradly-marketplace",
  "children": [{
    "title": "Step-by-Step Process",
    "slug": "step-by-step-process",
    "children": [{
      "title": "1. Accessing the Marketplace",
      "slug": "1-accessing-the-marketplace"
    }, {
      "title": "2. Creating a Store",
      "slug": "2-creating-a-store"
    }, {
      "title": "3. Adding Your Template",
      "slug": "3-adding-your-template"
    }, {
      "title": "4. Selecting the Category",
      "slug": "4-selecting-the-category"
    }, {
      "title": "5. Filling in Additional Attributes",
      "slug": "5-filling-in-additional-attributes"
    }, {
      "title": "6. Submitting Your Listing",
      "slug": "6-submitting-your-listing"
    }]
  }, {
    "title": "Additional Support",
    "slug": "additional-support",
    "children": []
  }]
}];

const layoutProps = {
  Layout,
meta,
slug,
tableOfContents
};
const MDXLayout = _Default
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">






    <_Heading level={2} id={"submitting-your-template-to-the-tradly-marketplace"} nextElement={{
      "type": "paragraph"
    }}>Submitting Your Template to the Tradly Marketplace</_Heading>
    <p>{`This guide outlines the steps to submit your template to the Tradly Marketplace.`}</p>
    <_Heading level={3} id={"step-by-step-process"} nextElement={{
      "type": "heading",
      "depth": 4
    }}>Step-by-Step Process</_Heading>
    <_Heading level={4} id={"1-accessing-the-marketplace"} nextElement={{
      "type": "list",
      "ordered": true,
      "start": 1,
      "spread": false
    }}>1. Accessing the Marketplace</_Heading>
    <ol {...{
      "role": "list"
    }}>
      <li parentName="ol">{`Go to `}<a parentName="li" {...{
          "href": "https://marketplace.tradly.app"
        }}>{`marketplace.tradly.app`}</a>{`.`}</li>
      <li parentName="ol">{`Log in using your Tradly account credentials.`}</li>
    </ol>
    <_Heading level={4} id={"2-creating-a-store"} nextElement={{
      "type": "list",
      "ordered": true,
      "start": 1,
      "spread": false
    }}>2. Creating a Store</_Heading>
    <ol {...{
      "role": "list"
    }}>
      <li parentName="ol">{`Create a store by providing your store name and description.`}</li>
      <li parentName="ol">{`Click on `}<strong parentName="li">{`Add Listing`}</strong>{` to open the add listing page.`}</li>
    </ol>
    <_Heading level={4} id={"3-adding-your-template"} nextElement={{
      "type": "list",
      "ordered": true,
      "start": 1,
      "spread": false
    }}>3. Adding Your Template</_Heading>
    <ol {...{
      "role": "list"
    }}>
      <li parentName="ol"><strong parentName="li">{`Full Image`}</strong>{`: Upload a full image of your website’s home page.`}</li>
      <li parentName="ol"><strong parentName="li">{`Name`}</strong>{`: Provide a proper name for your template.`}</li>
      <li parentName="ol"><strong parentName="li">{`Description`}</strong>{`: Write a detailed description of your template.`}</li>
      <li parentName="ol"><strong parentName="li">{`Slug`}</strong>{`: Enter a unique slug for your template.`}</li>
      <li parentName="ol"><strong parentName="li">{`Meta Title and Description`}</strong>{`: Provide meta title and description for SEO purposes.`}</li>
      <li parentName="ol"><strong parentName="li">{`Price`}</strong>{`: Set the price for your template. If you want to offer it for free, set the price to 0.`}</li>
    </ol>
    <_Heading level={4} id={"4-selecting-the-category"} nextElement={{
      "type": "list",
      "ordered": true,
      "start": 1,
      "spread": false
    }}>4. Selecting the Category</_Heading>
    <ol {...{
      "role": "list"
    }}>
      <li parentName="ol">{`From the category list, select a category with the “template” prefix.`}</li>
      <li parentName="ol">{`After selecting a category, additional attributes will appear.`}</li>
    </ol>
    <_Heading level={4} id={"5-filling-in-additional-attributes"} nextElement={{
      "type": "list",
      "ordered": true,
      "start": 1,
      "spread": false
    }}>5. Filling in Additional Attributes</_Heading>
    <ol {...{
      "role": "list"
    }}>
      <li parentName="ol"><strong parentName="li">{`Short Description`}</strong>{`: Write a short description (less than 150 words).`}</li>
      <li parentName="ol"><strong parentName="li">{`Type`}</strong>{`: Select “web” as the type.`}</li>
      <li parentName="ol"><strong parentName="li">{`Template Domain Address`}</strong>{`: Enter your domain address (without www. or https).`}</li>
      <li parentName="ol"><strong parentName="li">{`Demo Link`}</strong>{`: Provide a demo link for your template.`}</li>
      <li parentName="ol"><strong parentName="li">{`Payment Link`}</strong>{`: If your template is not free, submit a payment link (Stripe or PayPal). You can also contact us for payment assistance.`}</li>
      <li parentName="ol"><strong parentName="li">{`Module`}</strong>{`: Select the module type of your template (e.g., product, event, SaaS).`}</li>
    </ol>
    <_Heading level={4} id={"6-submitting-your-listing"} nextElement={{
      "type": "list",
      "ordered": true,
      "start": 1,
      "spread": false
    }}>6. Submitting Your Listing</_Heading>
    <ol {...{
      "role": "list"
    }}>
      <li parentName="ol">{`Click on `}<strong parentName="li">{`Add Listing`}</strong>{` to submit your template.`}</li>
      <li parentName="ol">{`Your submission will initially be marked as “pending.”`}</li>
      <li parentName="ol">{`Our team will review your submission carefully. If there are any issues, we will inform you.`}</li>
      <li parentName="ol">{`Once approved, your template will be published on the marketplace.`}</li>
    </ol>
    <img class="w-auto  h-full object-cover rounded-lg shadow-lg" alt="Add Listings" src="/img/submit-listing.png" />
    <_Heading level={3} id={"additional-support"} nextElement={{
      "type": "list",
      "ordered": false,
      "start": null,
      "spread": false
    }}>Additional Support</_Heading>
    <ul {...{
      "role": "list"
    }}>
      <li parentName="ul">{`For any issues or questions, contact us after submitting your template.`}</li>
      <li parentName="ul">{`Our team is available to assist you with payment setup and other concerns.`}</li>
    </ul>
    <p>{`By following these steps, you can successfully submit your template to the Tradly Marketplace and make it available to a wider audience. For further details and support, refer to the Tradly documentation or contact our support team.`}</p>



    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
MDXContent.layoutProps = layoutProps
